import {Component, EventEmitter, Input, Output} from "@angular/core";
import {CommonModule} from "@angular/common";
import {MatTableModule} from "@angular/material/table";
import {MatIconModule} from "@angular/material/icon";
import {MatIconButton, MatButton} from "@angular/material/button";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatChipsModule} from "@angular/material/chips";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatDividerModule} from "@angular/material/divider";
import {MatCardModule} from "@angular/material/card";
import {MatTooltip} from "@angular/material/tooltip";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";

import {IPropertyStructure} from "../../../interfaces/i-property-structure";
import {IFeatureChip} from "../../../interfaces/i-feature-chip";
import {ViewerConstants} from "../../../../shared/model/viewer-constants";
import {DocumentService} from "../../../services/internal-services/document.service";
import {ROOF_SCORE_MAP, COLOR_GREEN, COLOR_YELLOW, COLOR_RED} from "../../../model/roof-score.constants";
import {pathToLabel} from "../../../utils/property-object-flattener";
import {buildDetectedChips, buildCountChips} from "../../../utils/feature-chips.util";
import {formatArea, formatLength, formatVolume } from "../../../utils/format-utils";
import {FIELD_OVERRIDES} from "../../../config/property-field-overrides.config";

@Component({
    selector: "lib-structure-card",
    standalone: true,
    templateUrl: "./structure-card.component.html",
    styleUrls: ["./structure-card.component.scss"],
    imports: [
        CommonModule,
        MatIconModule,
        MatIconButton,
        MatButton,
        MatExpansionModule,
        MatChipsModule,
        MatProgressBarModule,
        MatDividerModule,
        MatCardModule,
        MatTableModule,
        MatTooltip,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
    ],
})
export class StructureCardComponent {
    @Input() structure: IPropertyStructure;

    /** WKT geometry of the property parcel, used to zoom to the parcel boundary. */
    @Input() parcelWkt: string | undefined;

    /** Display index (1-based label shown in the card title). */
    @Input() index: number;

    /** Current measurement system ID (0 = Imperial, 1 = Metric). */
    @Input() measurementSystem: number;

    readonly measurementSystems = ViewerConstants.MEASUREMENT_SYSTEMS.IDS;

    @Input() expanded = true;

    /** Emits when the user clicks a zoom button, carrying the WKT geometry and padding. */
    @Output() zoomRequested = new EventEmitter<{ wkt: string; paddingPx: number }>();

    showAllRoofFeatures = false;
    showAllDiscoloration = false;
    showAllStructureFeatures = false;

    protected get isImperial(): boolean {
        return this.measurementSystem === this.measurementSystems.IMPERIAL;
    }

    /** Emits a zoom request for the given WKT geometry. */
    onZoomClick(wkt: string, paddingPx: number): void {
        this.zoomRequested.emit({wkt, paddingPx});
    }

    /** Copies the given WKT value to the clipboard. */
    copyToClipboard(value: string): void {
        DocumentService.copyTextToClipboard(value);
    }

    getRoofScoreColor(value: number): string {
        return ROOF_SCORE_MAP[value]?.color ?? "#9e9e9e";
    }

    getRoofScoreLabel(value: number): string {
        return ROOF_SCORE_MAP[value]?.label ?? "N/A";
    }

    getDsColor(percent: number | null): string | null {
        if (percent == null) return null;
        if (percent <= 20) return COLOR_GREEN;
        if (percent <= 49) return COLOR_YELLOW;
        return COLOR_RED;
    }

    formatArea(m2: number): string {
        return formatArea(m2, this.isImperial);
    }

    formatVolume(m3: number): string {
        return formatVolume(m3, this.isImperial);
    }

    formatLength(m: number): string {
        return formatLength(m, this.isImperial);
    }

    formatBuildingType(value: string): string {
        return value ? pathToLabel(value) : "–";
    }

    getDefensibleSpaceBands(report: any): { label: string; t: number | null; b: number | null }[] {
        return [
            {label: "5 ft", t: report?.DST5 ?? null, b: report?.DSB5 ?? null},
            {label: "30 ft", t: report?.DST30 ?? null, b: report?.DSB30 ?? null},
            {label: "100 ft", t: report?.DST100 ?? null, b: report?.DSB100 ?? null},
            {label: "200 ft", t: report?.DST200 ?? null, b: report?.DSB200 ?? null},
        ];
    }

    getDiscolorationChips(types: Record<string, any>): IFeatureChip[] {
        if (!types) return [];
        return buildDetectedChips(types)
            .filter((c) => c.detected || this.showAllDiscoloration)
            .sort((a, b) => Number(b.detected) - Number(a.detected));
    }

    getStructureFeatures(features: Record<string, { detected: boolean }>): IFeatureChip[] {
        if (!features) return [];
        return Object.entries(features)
            .map(([key, v]) => ({
                label: FIELD_OVERRIDES[`structure_features.${key}`]?.label ?? pathToLabel(key),
                detected: !!v?.detected,
            }))
            .filter((c) => c.detected || this.showAllStructureFeatures)
            .sort((a, b) => Number(b.detected) - Number(a.detected));
    }

    getRoofElementChips(elements: Record<string, any>, roofSolar?: any): IFeatureChip[] {
        if (!elements) return [];
        const {solar_panel: solarPanelData, ...otherElements} = elements;
        const chips = buildCountChips(otherElements, "roof_elements", {}, this.formatArea.bind(this));
        const hasSubtypeData =
            solarPanelData &&
            typeof solarPanelData === "object" &&
            Object.values(solarPanelData as Record<string, any>).some(
                (sv: any) => sv && typeof sv === "object" && (sv.count != null || sv.area != null),
            );
        if (hasSubtypeData) {
            chips.push(...buildCountChips({solar_panel: solarPanelData}, "roof_elements", {}, this.formatArea.bind(this)));
        } else if (roofSolar?.value && roofSolar.value !== "false") {
            chips.push({label: "Solar Panel", detected: true, count: null, area: null});
        }
        return chips
            .filter((c) => c.detected || this.showAllRoofFeatures)
            .sort((a, b) => Number(b.detected) - Number(a.detected));
    }
}
